import { SectionCard } from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import type { OrderDetail } from '@/lib/api-admin/types';

/**
 * QA-144 [D-18 / ID144] — BO 주문상세 "응모 입력값" 섹션.
 *
 * be-event 계약(OrderDetailResponse.eventInputs, order 43a5ec1 + catalog 5287f16):
 *   eventInputs?: [{ label, value }]  // @JsonInclude NON_NULL
 * 이벤트 응모 상품의 입력값(멤버선택/채널/국적/사인/생년월일 등)을 항목명(label)과 값(value)으로,
 * catalog 수집필드 정렬 순서대로 노출한다.
 *   - label 은 표시용 항목명(label_snapshot) — 내부 fieldKey 는 노출하지 않는다.
 *   - 민감값(생년월일/연락처 등)은 BE 가 마스킹(********)해 내려보내므로 그대로 표기한다.
 *   - 응모 없는 일반 주문은 eventInputs 키 자체가 없음(undefined) → 섹션을 렌더하지 않는다.
 *
 * `eventInputs` 는 generated OpenAPI 스펙에 미반영(codegen lag)이라 OrderDetail 을 로컬 타입으로
 * 확장해 읽는다(OrderDetailPage 의 displayLabelCode 패턴과 동일).
 */
interface EventInput {
  readonly label: string;
  readonly value: string;
}

type OrderWithEventInputs = OrderDetail & {
  readonly eventInputs?: ReadonlyArray<EventInput> | null;
};

interface EventApplicationInputsSectionProps {
  order: OrderDetail;
}

export const EventApplicationInputsSection = ({
  order,
}: EventApplicationInputsSectionProps) => {
  const { t } = useTranslation('orders');
  const eventInputs = (order as OrderWithEventInputs).eventInputs ?? [];
  // 응모 입력값이 없는 주문(일반 상품)은 섹션 자체를 숨겨 빈 카드 노이즈를 줄인다.
  if (eventInputs.length === 0) return null;

  return (
    <SectionCard
      title={t('orders.detail.eventApplicationInputs', '응모 입력값')}
      data-testid="event-application-inputs"
    >
      <ul className="space-y-2" data-sentry-mask>
        {eventInputs.map((input, idx) => (
          <li
            key={`${input.label}-${idx}`}
            className="grid grid-cols-[140px_1fr] items-baseline gap-3 text-sm"
          >
            <span className="text-[color:var(--content-muted)]">{input.label}</span>
            <span className="text-[color:var(--content-strong)]">{input.value || '-'}</span>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
};
